/**
 * Esqueletos no formato real de cada tela (Frente 6, 25/10/2026).
 *
 * Antes: o lazy-route mostrava um spinner no meio da tela vazia, e a guarda
 * de rota escrevia "Carregando sua conta..." no lugar do painel inteiro.
 * Quando a tela chegava, topo, dock e conteúdo apareciam de uma vez, e tudo
 * pulava. Aqui o esqueleto usa as MESMAS classes da casca (`.shell
 * .suite-modern`, `.topbar`, `.content`, `.topo-secao`, `.page-head`,
 * `.grid-4 > .stat`, `.navegacao-suite`), então ocupa as mesmas medidas que a
 * tela de verdade vai ocupar - em qualquer largura, porque as regras de
 * celular dessas classes valem para ele também.
 *
 * As formas seguem o que as telas desenham hoje:
 *   inicio   Início: como o painel, sem a pílula da trilha
 *   painel   Painel do CRM, Marketing, Flow: topo, 4 números, gráfico
 *   lista    Clientes, contatos, leads, campanhas: topo, filtros, linhas
 *   config   Configurações e Console: topo da seção e formulário largo
 *   caixa    Conversas do Flow e caixas: lista à esquerda, conversa à direita
 *   detalhe  Ficha de um registro: cabeçalho com avatar, abas, dados e linha do tempo
 *   formulario  Editor e criação: blocos de campos e a barra de salvar
 *   quadro   Funil (kanban): colunas com cartões, rolando de lado no celular
 * As três últimas entraram na F1 (26/10/2026) para as frentes de produto.
 *
 * Fora do leitor de tela (`aria-hidden`); quem lê por áudio recebe só o
 * `aria-busy` + rótulo do contêiner. O brilho entra 120ms depois, para não
 * piscar na rede boa, e para com `prefers-reduced-motion`.
 */
import type { CSSProperties, ReactElement } from 'react';

export type FormaDeTela = 'inicio' | 'painel' | 'lista' | 'config' | 'caixa' | 'detalhe' | 'formulario' | 'quadro';

/** Qual forma a rota vai ter. Recebe o caminho com ou sem o slug do cliente. */
export function formaDaRota(caminho: string): FormaDeTela {
  const c = caminho.toLowerCase();
  if (/\/settings(\/|$)/.test(c)) return 'config';
  if (/\/(flw\/inbox|mail)(\/|$)/.test(c)) return 'caixa';
  if (/\/home\/?$/.test(c)) return 'inicio';
  if (/\/(new|novo|editar|edit|editor)(\/|$)/.test(c)) return 'formulario';
  if (/\/(contacts|contatos|clientes)\/[^/]+\/?$/.test(c)) return 'detalhe';
  if (/\/(crm|mkt\/dashboard|flw\/dashboard|pop\/dashboard|bio\/dashboard)\/?$/.test(c)) return 'painel';
  if (/\/(crm|mkt|flw|pop|bio)\/?$/.test(c)) return 'painel';
  return 'lista';
}

const L = ({ w, h = 14, r, style }: { w: string | number; h?: number; r?: number; style?: CSSProperties }) => (
  <span className="skeleton esq-peca" style={{ width: w, height: h, ...(r !== undefined ? { borderRadius: r } : {}), ...style }} />
);

/** Topo da seção: a pílula da trilha, o título e a linha de apoio. */
function TopoDaSecao({ comPilula = true }: { comPilula?: boolean }) {
  return (
    <div className="topo-secao esq-topo">
      {comPilula && <L w="min(320px,80%)" h={48} r={999} />}
      <div className="page-head">
        <div className="esq-bloco">
          <L w="min(300px,70vw)" h={40} r={12} />
          <L w="min(420px,80vw)" h={16} />
        </div>
      </div>
    </div>
  );
}

function Painel({ comPilula = true }: { comPilula?: boolean }) {
  return (
    <div className="stack esq-pagina">
      <TopoDaSecao comPilula={comPilula} />
      <div className="grid grid-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="stat esq-cartao">
            <L w="55%" h={12} />
            <L w="70%" h={36} r={10} style={{ marginTop: 22 }} />
            <L w="45%" h={12} style={{ marginTop: 16 }} />
          </div>
        ))}
      </div>
      <div className="card esq-cartao esq-grafico">
        <L w="min(240px,60%)" h={18} />
        <L w="min(320px,80%)" h={12} style={{ marginTop: 10 }} />
        <div className="esq-graf" style={{ marginTop: 28 }}>
          {[38, 62, 48, 80, 56, 92, 70].map((h, i) => (
            <span key={i} className="skeleton" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Lista() {
  return (
    <div className="stack esq-pagina">
      <TopoDaSecao />
      <div className="row esq-filtros">
        {[96, 72, 96, 120].map((w, i) => (
          <L key={i} w={w} h={36} r={999} />
        ))}
      </div>
      <div className="card esq-cartao esq-linhas">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="esq-linha-tabela">
            <L w={40} h={40} r={999} />
            <div className="esq-bloco" style={{ flex: 1 }}>
              <L w={`${62 - i * 5}%`} h={14} />
              <L w={`${40 - i * 3}%`} h={11} />
            </div>
            <L w={88} h={28} r={999} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* Configurações e Console: a pílula da seção no topo e um formulário largo.
   A coluna de abas à esquerda saiu na F4 (as seções foram para a pílula);
   desenhar a coluna fazia a tela pular quando o dado chegava. */
function Config() {
  return (
    <div className="stack esq-pagina">
      <TopoDaSecao />
      {[4, 2].map((n, b) => (
        <div key={b} className="card esq-cartao stack">
          <L w="min(220px,55%)" h={18} />
          <L w="min(420px,80%)" h={12} />
          <div className="esq-campos">
            {Array.from({ length: n }, (_, i) => (
              <div key={i} className="esq-bloco">
                <L w={96} h={12} />
                <L w="100%" h={44} r={12} />
              </div>
            ))}
          </div>
          <L w={120} h={44} r={999} style={{ alignSelf: 'flex-end' }} />
        </div>
      ))}
    </div>
  );
}

function Caixa() {
  return (
    <div className="stack esq-pagina">
      <TopoDaSecao />
      <div className="esq-caixa">
        <div className="card esq-cartao esq-caixa__lista">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="esq-linha-tabela">
              <L w={36} h={36} r={999} />
              <div className="esq-bloco" style={{ flex: 1 }}>
                <L w={`${70 - i * 6}%`} h={13} />
                <L w="85%" h={11} />
              </div>
            </div>
          ))}
        </div>
        <div className="card esq-cartao esq-caixa__conversa">
          <L w="40%" h={18} />
          <div className="esq-bloco" style={{ marginTop: 'auto' }}>
            <L w="55%" h={44} r={18} />
            <L w="42%" h={44} r={18} style={{ alignSelf: 'flex-end' }} />
            <L w="100%" h={48} r={999} style={{ marginTop: 12 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Detalhe() {
  return (
    <div className="stack esq-pagina">
      <div className="card esq-cartao esq-detalhe__topo">
        <L w={64} h={64} r={999} />
        <div className="esq-bloco" style={{ flex: 1 }}>
          <L w="min(320px,70%)" h={28} r={10} />
          <L w="min(220px,50%)" h={13} />
        </div>
        <L w={120} h={42} r={999} />
      </div>
      <div className="row esq-filtros">
        {[88, 104, 96].map((w, i) => <L key={i} w={w} h={38} r={999} />)}
      </div>
      <div className="esq-detalhe">
        <div className="card esq-cartao stack">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="esq-bloco">
              <L w={80} h={11} />
              <L w={`${70 - i * 8}%`} h={15} />
            </div>
          ))}
        </div>
        <div className="card esq-cartao stack">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="esq-linha-tabela">
              <L w={28} h={28} r={999} />
              <div className="esq-bloco" style={{ flex: 1 }}>
                <L w={`${64 - i * 6}%`} h={13} />
                <L w="30%" h={11} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Formulario() {
  return (
    <div className="stack esq-pagina">
      <TopoDaSecao comPilula={false} />
      {[3, 2].map((n, b) => (
        <div key={b} className="card esq-cartao stack">
          <L w="min(200px,50%)" h={18} />
          <div className="esq-campos">
            {Array.from({ length: n }, (_, i) => (
              <div key={i} className="esq-bloco">
                <L w={96} h={12} />
                <L w="100%" h={44} r={12} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="row esq-barra-salvar">
        <L w={110} h={44} r={999} />
        <L w={130} h={44} r={999} />
      </div>
    </div>
  );
}

function Quadro() {
  return (
    <div className="stack esq-pagina">
      <TopoDaSecao />
      <div className="esq-quadro">
        {[4, 3, 2, 3, 1].map((n, c) => (
          <div key={c} className="esq-quadro__coluna">
            <L w="60%" h={14} />
            {Array.from({ length: n }, (_, i) => (
              <div key={i} className="card esq-cartao esq-bloco">
                <L w="80%" h={14} />
                <L w="50%" h={11} />
                <L w="40%" h={22} r={999} style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const FORMAS: Record<FormaDeTela, () => ReactElement> = { inicio: () => <Painel comPilula={false} />, painel: () => <Painel />, lista: Lista, config: Config, caixa: Caixa, detalhe: Detalhe, formulario: Formulario, quadro: Quadro };

/** Só o miolo da página: para quando a casca já está na tela. */
export function EsqueletoDePagina({ forma }: { forma: FormaDeTela }) {
  const Forma = FORMAS[forma];
  return (
    <div className="esqueleto-tela" role="status" aria-busy="true" aria-label="Carregando a tela">
      <div aria-hidden="true">
        <Forma />
      </div>
    </div>
  );
}

/** Casca inteira (topo, conteúdo, dock): enquanto a conta ou a casca carregam. */
export function EsqueletoDaCasca({ forma }: { forma: FormaDeTela }) {
  return (
    <div className="shell suite-modern esqueleto-casca" role="status" aria-busy="true" aria-label="Carregando sua conta">
      <div aria-hidden="true">
        <header className="topbar">
          <span className="suite-topo-marca"><L w={128} h={26} r={8} /></span>
          <div className="topo-acoes esq-topo-acoes">
            <L w={144} h={42} r={999} style={{ flex: 'none' }} />
            <L w={42} h={42} r={999} />
          </div>
        </header>
        <main className="content">
          <EsqueletoDePaginaSemRotulo forma={forma} />
        </main>
        <div className="esq-dock"><L w={234} h={54} r={999} /></div>
      </div>
    </div>
  );
}

function EsqueletoDePaginaSemRotulo({ forma }: { forma: FormaDeTela }) {
  const Forma = FORMAS[forma];
  return (
    <div className="esqueleto-tela">
      <Forma />
    </div>
  );
}

/** As portas de entrada: palco de marca e cartão do formulário. */
export function EsqueletoDeAcesso() {
  return (
    <div className="app-auth suite-modern auth-hub--suite esqueleto-acesso" role="status" aria-busy="true" aria-label="Carregando">
      <aside className="auth-marca" aria-hidden="true">
        <L w={110} h={22} r={8} />
        <div className="auth-org esq-bloco">
          <L w={160} h={11} />
          <L w="min(380px,90%)" h={40} r={12} />
          <L w="min(300px,70%)" h={14} />
        </div>
        <span />
      </aside>
      <div className="auth-lado" aria-hidden="true">
        <div className="auth-card stack">
          <L w="50%" h={34} r={10} />
          <L w="80%" h={14} />
          {[0, 1].map((i) => (
            <div key={i} className="esq-bloco">
              <L w={70} h={12} />
              <L w="100%" h={48} r={14} />
            </div>
          ))}
          <L w="100%" h={50} r={999} />
        </div>
      </div>
    </div>
  );
}
